/**
 * An event, as a calendar file.
 *
 * A game night that only exists inside one app is a game night people miss.
 * This writes the one file every calendar on every platform understands, with
 * the time in UTC so it lands at the right local hour whichever machine opens
 * it - the single thing these files most often get wrong.
 */

type CalEvent = {
  id: string;
  title: string;
  description?: string;
  startsAt: number;
  endsAt: number;
  where?: string;
};

/** 2026-09-18T19:30:00Z as calendars want it: 20260918T193000Z. */
function stamp(ms: number): string {
  return new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/** Line breaks, commas and semicolons all mean something in this format. */
function esc(text: string): string {
  return String(text).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1');
}

export function toIcs(event: CalEvent): string {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Pugo//Game night//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    'UID:' + event.id + '@pugo.ink',
    'DTSTAMP:' + stamp(Date.now()),
    'DTSTART:' + stamp(event.startsAt),
    'DTEND:' + stamp(event.endsAt),
    'SUMMARY:' + esc(event.title),
    event.description ? 'DESCRIPTION:' + esc(event.description) : '',
    event.where ? 'LOCATION:' + esc(event.where) : '',
    /* A reminder fifteen minutes before, which is what somebody adding a game
       night to their calendar is actually after. */
    'BEGIN:VALARM',
    'TRIGGER:-PT15M',
    'ACTION:DISPLAY',
    'DESCRIPTION:' + esc(event.title),
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter(Boolean).join('\r\n');
}

/** Hands the file to the browser, which hands it to the calendar. */
export function downloadIcs(event: CalEvent, filename?: string): void {
  const blob = new Blob([toIcs(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (filename || event.title.replace(/[^\w -]+/g, '').trim() || 'game-night') + '.ics';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
